import layout from '../EventPage/EventPage.module.css';
import styles from './CheckoutSkeleton.module.css';

/**
 * One shimmer bar. Widths vary so the placeholder reads like real text.
 * @param props - Bar width and an optional size class
 * @returns A decorative shimmer block
 * @example <SkeletonBar width="40%" className={styles.title} />
 */
function SkeletonBar({ width, className }: { width: string; className?: string }) {
  return <div className={`${styles.block} ${className ?? styles.line}`} style={{ width }} />;
}

/**
 * Placeholder for the event payment and checkout steps while the booking preview
 * loads. Mirrors those pages: the Details card leads, the Payment card and action
 * trail, and the order summary sits in the sidebar. It uses the same
 * summaryAsideLayout, so it lands in the same place on desktop, when stacked on
 * narrow viewports, and inside the embed.
 * @returns The loading skeleton, announced to assistive tech as "Loading checkout…"
 * @example <CheckoutSkeleton />
 */
export function CheckoutSkeleton() {
  return (
    <div role="status" className={`${layout.twoColumnLayout} ${layout.summaryAsideLayout}`}>
      <span className="dorsia-visually-hidden">Loading checkout…</span>

      <div className={layout.summaryAsideStack} aria-hidden="true">
        <div className={layout.summaryAsideLead}>
          <div className={layout.card}>
            <div className={styles.lines}>
              <SkeletonBar width="28%" className={styles.title} />
              <SkeletonBar width="22%" />
              <SkeletonBar width="38%" />
              <SkeletonBar width="46%" />
            </div>
          </div>
        </div>

        <div className={layout.summaryAsideTrail}>
          <div className={`${layout.card} ${layout.mt20}`}>
            <div className={styles.lines}>
              <SkeletonBar width="30%" className={styles.title} />
              <SkeletonBar width="100%" className={styles.field} />
              <SkeletonBar width="100%" className={styles.field} />
              <SkeletonBar width="100%" className={styles.button} />
            </div>
          </div>
        </div>
      </div>

      <div className={layout.sidebarColumn} aria-hidden="true">
        <div className={layout.orderSummary}>
          <div className={styles.lines}>
            <SkeletonBar width="58%" className={styles.title} />
            <SkeletonBar width="82%" />
          </div>
          <div className={layout.orderSummaryDivider} />
          <div className={styles.media}>
            <div className={`${styles.block} ${styles.thumb}`} />
            <div className={`${styles.lines} ${styles.grow}`}>
              <SkeletonBar width="64%" />
              <SkeletonBar width="42%" />
              <SkeletonBar width="30%" />
            </div>
          </div>
          <div className={layout.orderSummaryDivider} />
          <div className={styles.split}>
            <SkeletonBar width="34%" />
            <SkeletonBar width="16%" />
          </div>
          <div className={styles.split}>
            <SkeletonBar width="40%" className={styles.total} />
            <SkeletonBar width="22%" className={styles.total} />
          </div>
        </div>
      </div>
    </div>
  );
}
